import { inject, Injectable } from "@angular/core";
import { TLayer } from "../../../types/explorer/sjmap2/TLayer";
import { Store } from "@ngrx/store";
import { MapLibreLayersService } from "../../shared/maplibreLayers.service";
import { TMapLibreLayerId } from "../../../types/explorer/sjmap2/TMapLibreLayerId";
import { Map } from "maplibre-gl";
import { IRasterLayer } from "../../../interfaces/sjmap2/raster/IRaster";
import { IRasterSource } from "../../../interfaces/sjmap2/raster/IRasterSource";
import { TRasterMapLibreLayer, TRasterMapLibreLayerId } from "../../../types/explorer/sjmap2/TRasterMapLibre";
import { IVectorLayer } from "../../../interfaces/sjmap2/vector/IVector";
import { TVectorMapLibreLayer, TVectorMapLibreLayerId, TVectorMapLibreLayers } from "../../../types/explorer/sjmap2/TVectorMapLibre";
import { IVectorSource } from "../../../interfaces/sjmap2/vector/IVectorSource";

@Injectable({
  providedIn: 'root'
})
export class ExplorerReorderSJMapLayersOnMapService {

    private maplibreLayersService: MapLibreLayersService = inject(MapLibreLayersService);

    constructor() { }
    
    reorderSJMapLayersOnMap(_map: Map, SJMapLayers: TLayer[]) {

        SJMapLayers.forEach((currSJMLayer: TLayer, index: number) => {
            const prevSJMLayer = SJMapLayers[index - 1] ?? null; // Prendi il layer precedente (nella SJMap) o null se non c'è
            if (prevSJMLayer) {
              this.moveSJMapLayerOnMap(_map, currSJMLayer, prevSJMLayer); // Sposta il layer sotto il layer precedente
            } else {
              this.moveSJMapLayerOnMap(_map, currSJMLayer); // Sposta il layer sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }
        });
         
    }

    private moveSJMapLayerOnMap(_map: Map, SJMapLayer: TLayer, prevSJMapLayer?: TLayer): void {

        if (!this.maplibreLayersService.hasMapLibreData(SJMapLayer)) {
            console.error(`Impossibile spostare il layer ${SJMapLayer.uuid} sulla mappa: non ha dati per MapLibre. Layer:`, SJMapLayer);
            return;
        }

        if (SJMapLayer.type === "raster") {
            if(prevSJMapLayer) {

                if(!this.maplibreLayersService.hasMapLibreData(prevSJMapLayer)) {
                    console.error(`Impossibile spostare il layer ${SJMapLayer.uuid} sulla mappa: il layer precedente ${prevSJMapLayer.uuid} non ha dati per MapLibre. Layer precedente:`, prevSJMapLayer);
                    return;
                }

                // Prendi l'id del MapLibreLayer più in basso del SJMapLayer precedente (il MapLibreLayer che sta sotto a tutti gli altri)
                const prevSJMLayerBottomMLLayerId: TMapLibreLayerId = this.maplibreLayersService.getBottomMLLayerIdFromSJMLayer(prevSJMapLayer);
        
                this.moveSJMapRasterLayerOnMap(_map, SJMapLayer, prevSJMLayerBottomMLLayerId); // Sposta il layer raster sotto l'ultimo MapLibreLayer del SJMapLayer precedente
            } else {
                this.moveSJMapRasterLayerOnMap(_map, SJMapLayer); // Sposta il layer raster sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }
        } else if (SJMapLayer.type === 'vector') {
            if(prevSJMapLayer) {
    
                if(!this.maplibreLayersService.hasMapLibreData(prevSJMapLayer)) {
                    console.error(`Impossibile spostare il layer ${SJMapLayer.uuid} sulla mappa: il layer precedente ${prevSJMapLayer.uuid} non ha dati per MapLibre. Layer precedente:`, prevSJMapLayer);
                    return;
                }
        
                // Prendi l'id del MapLibreLayer più in basso del SJMapLayer precedente (il MapLibreLayer che sta sotto a tutti gli altri)
                const prevSJMLayerBottomId: TMapLibreLayerId = this.maplibreLayersService.getBottomMLLayerIdFromSJMLayer(prevSJMapLayer);

                this.moveSJMapVectorLayerOnMap(_map, SJMapLayer, prevSJMLayerBottomId); // Sposta il layer vector sotto l'ultimo MapLibreLayer del SJMapLayer precedente
            } else {
                this.moveSJMapVectorLayerOnMap(_map, SJMapLayer); // Sposta il layer vector sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }
        }

    }

    private moveSJMapRasterLayerOnMap(_map: Map, SJMapRasterLayer: IRasterLayer, upperMapLibreLayerId?: TMapLibreLayerId): void {

        SJMapRasterLayer.sources.forEach((SJMRSource: IRasterSource, index, SJMRSources: IRasterSource[]) => {
            const prevSJMRSource = SJMRSources[index - 1] ?? null; // Prendi la SJMapRasterSource precedente o null se non c'è
            if(prevSJMRSource) {
                const prevRasterLayerId: TRasterMapLibreLayerId = prevSJMRSource.maplibre.layer; // Prendi l'id del layer raster precedente (PER LA STRUTTURA ATTUALE IL LAYER DEFINISCE IL SUO STESSO ID, LO RIMANGO QUI IN CASO CAMBIERMO MAI LA STRUTTURA)
                this.moveRasterLayerOnMap(_map, SJMRSource.maplibre.layer, prevRasterLayerId) // Sposta il layer raster della Source attuale sotto il layer della Source precedente (nel SJMapLayer attuale)
            } else if(upperMapLibreLayerId) {
                this.moveRasterLayerOnMap(_map, SJMRSource.maplibre.layer, upperMapLibreLayerId) // Sposta il layer raster della Source attuale sotto l'ultimo layer di MapLibre del SJMapLayer precedente
            } else {
                this.moveRasterLayerOnMap(_map, SJMRSource.maplibre.layer) // Sposta il layer raster della Source attuale sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }
        });

    }

    // Se cambia la struttura del layer servirà
    // private moveRasterLayersOnMap(_map: Map, rasterLayers: TRasterMapLibreLayers, upperMapLibreLayerId?: TMapLibreLayerId): void {
    // 
    //     rasterLayers.forEach((rasterLayer: TRasterMapLibreLayer, index, rasterLayers: TRasterMapLibreLayers) => {
    //         const prevRasterLayer = rasterLayers[index - 1] ?? null; // Prendi il layer precedente o null se non c'è
    //         if(prevRasterLayer) {
    //             const prevRasterLayerId: TRasterMapLibreLayerId = prevRasterLayer; // Prendi l'id del layer precedente (PER LA STRUTTURA ATTUALE IL LAYER DEFINISCE IL SUO STESSO ID, LO RIMANGO QUI IN CASO CAMBIEREMO MAI LA STRUTTURA)
    //             this.moveRasterLayerOnMap(_map, rasterLayer, prevRasterLayerId); // Sposta il layer raster attuale sotto il layer raster precedente (nella source attuale)
    //         } else if(upperMapLibreLayerId) {
    //             this.moveRasterLayerOnMap(_map, rasterLayer, upperMapLibreLayerId); // Sposta il layer raster attuale sotto l'ultimo layer di MapLibre della source precedente
    //         } else {
    //             this.moveRasterLayerOnMap(_map, rasterLayer); // Sposta il layer raster attuale sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
    //         }
    //     });
    // 
    // }

    private moveRasterLayerOnMap(_map: Map, rasterLayer: TRasterMapLibreLayer, upperMapLibreLayerId?: TMapLibreLayerId): void {
        const rasterLayerId: TRasterMapLibreLayerId = rasterLayer; // Prendi l'id del layer raster attuale (PER LA STRUTTURA ATTUALE IL LAYER DEFINISCE IL SUO STESSO ID, LO RIMANGO QUI IN CASO CAMBIEREMO MAI LA STRUTTURA)
        if (upperMapLibreLayerId) {
            this.maplibreLayersService.moveLayerOnMap(_map, rasterLayerId, upperMapLibreLayerId);
        } else {
            this.maplibreLayersService.moveLayerOnMap(_map, rasterLayerId);
        }
    }

    private moveSJMapVectorLayerOnMap(_map: Map, SJMapVectorLayer: IVectorLayer, upperMapLibreLayerId?: TMapLibreLayerId): void {

        SJMapVectorLayer.sources.forEach((SJMVSource: IVectorSource, index, SJMVSources: IVectorSource[]) =>{
            const prevSJMVSource = SJMVSources[index - 1] ?? null; // Prendi la SJMapVectorSource precedente o null se non c'è
            if(prevSJMVSource) {
                const prevSJMVSourceLastLayerIndex: number = prevSJMVSource.maplibre.layers.length - 1; // Prendi l'indice dell'ultimo layer di MapLibre della Source precedente
                const prevSJMVSourceLastLayerId: TVectorMapLibreLayerId = prevSJMVSource.maplibre.layers[prevSJMVSourceLastLayerIndex]; // Prendi l'id dell'ultimo layer di MapLibre della Source precedente
                this.moveVectorLayersOnMap(_map, SJMVSource.maplibre.layers, prevSJMVSourceLastLayerId); // Sposta il layer vector della Source attuale sotto il layer della Source precedente (nel SJMapLayer attuale)
            } else if(upperMapLibreLayerId) {
                this.moveVectorLayersOnMap(_map, SJMVSource.maplibre.layers, upperMapLibreLayerId); // Sposta il layer vector della Source attuale sotto l'ultimo layer di MapLibre del SJMapLayer precedente
            } else {
                this.moveVectorLayersOnMap(_map, SJMVSource.maplibre.layers); // Sposta il layer vector della Source attuale sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }
        });

    }

    private moveVectorLayersOnMap(_map: Map, vectorLayers: TVectorMapLibreLayers, upperMapLibreLayerId?: TMapLibreLayerId): void {

        vectorLayers.forEach((vectorLayer: TVectorMapLibreLayer, index, vectorLayers: TVectorMapLibreLayers) => {
            const prevVectorLayer = vectorLayers[index - 1] ?? null; // Prendi il layer precedente o null se non c'è
            if(prevVectorLayer) {
                const prevVectorLayerId: TVectorMapLibreLayerId = prevVectorLayer; // Prendi l'id del layer precedente (PER LA STRUTTURA ATTUALE IL LAYER DEFINISCE IL SUO STESSO ID, LO RIMANGO QUI IN CASO CAMBIEREMO MAI LA STRUTTURA)
                this.moveVectorLayerOnMap(_map, vectorLayer, prevVectorLayerId); // Sposta il layer vector attuale sotto il layer vector precedente (nella source attuale)
            } else if(upperMapLibreLayerId) {
                this.moveVectorLayerOnMap(_map, vectorLayer, upperMapLibreLayerId); // Sposta il layer vector attuale sotto l'ultimo layer di MapLibre della source precedente
            } else {
                this.moveVectorLayerOnMap(_map, vectorLayer); // Sposta il layer vector attuale sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }
        });

    }

    private moveVectorLayerOnMap(_map: Map, vectorLayer: TVectorMapLibreLayer, upperMapLibreLayerId?: TMapLibreLayerId): void {
        const vectorLayerId: TVectorMapLibreLayerId = vectorLayer; // Prendi l'id del layer vector (PER LA STRUTTURA ATTUALE IL LAYER DEFINISCE IL SUO STESSO ID, LO RIMANGO QUI IN CASO CAMBIEREMO MAI LA STRUTTURA)
        if (upperMapLibreLayerId) {
            this.maplibreLayersService.moveLayerOnMap(_map, vectorLayerId, upperMapLibreLayerId);
        } else {
            this.maplibreLayersService.moveLayerOnMap(_map, vectorLayerId);
        }
    }
    
}
